import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import '../../styles/ui.css';

const MentionBox = React.forwardRef(function MentionBox({ pid, docs }, outerRef) {
  const containerRef = useRef(null);
  const textareaRef  = useRef(null);
  const mirrorRef    = useRef(null);

  React.useImperativeHandle(outerRef, () => textareaRef.current);

  const [open, setOpen]     = useState(false);
  const [start, setStart]   = useState(-1);
  const [q, setQ]           = useState('');
  const [ix, setIx]         = useState(0);
  const [pos, setPos]       = useState({ left: 8, top: 0, width: 160 });

  const norm = (s) =>
    String(s || '')
      .toLowerCase()
      .replace(/[^a-z0-9\s]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();

  const matches = useMemo(() => {
    if (!open || start < 0) return [];
    const list = Array.isArray(docs) ? docs : [];
    const qn = norm(q);
    if (!qn) return list.slice(0, 8);
    return list.filter((d) => norm(d.document_name).includes(qn)).slice(0, 8);
  }, [open, start, q, docs]);

  const computeDropdownPos = (val, caret) => {
    const ta = textareaRef.current;
    const container = containerRef.current;
    const mirror = mirrorRef.current;
    if (!ta || !container || !mirror) return;

    const cs = window.getComputedStyle(ta);
    const copy = (prop) => (mirror.style[prop] = cs.getPropertyValue(prop));
    mirror.style.position = 'absolute';
    mirror.style.visibility = 'hidden';
    mirror.style.whiteSpace = 'pre-wrap';
    mirror.style.wordBreak = 'break-word';
    mirror.style.overflowWrap = 'break-word';
    mirror.style.top = '0px';
    mirror.style.left = '0px';
    mirror.style.right = '0px';
    mirror.style.width = `${ta.clientWidth}px`;
    [
      'font', 'font-family', 'font-size', 'font-weight', 'font-style',
      'line-height', 'letter-spacing', 'text-indent', 'text-transform',
      'padding-top', 'padding-right', 'padding-bottom', 'padding-left',
      'border-top-width', 'border-right-width', 'border-bottom-width', 'border-left-width',
      'box-sizing'
    ].forEach(copy);

    const beforeStart = val.slice(0, start);
    const token       = val.slice(start, caret);
    const afterCaret  = val.slice(caret);

    mirror.innerHTML = '';
    mirror.append(document.createTextNode(beforeStart));
    const tokenSpan = document.createElement('span');
    tokenSpan.id = 'token-span';
    tokenSpan.textContent = token.length ? token : ' ';
    mirror.append(tokenSpan);
    mirror.append(document.createTextNode(afterCaret.length ? afterCaret : ' '));

    const tokenRect = tokenSpan.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();

    const left  = Math.max(8, tokenRect.left - containerRect.left);
    const top   = tokenRect.bottom - containerRect.top + 4;
    const width = Math.max( Math.round(tokenRect.width), 140 );

    setPos({ left, top, width });
  };

  const updateMentionState = (val, caret) => {
    const head = val.slice(0, caret);
    const a = head.lastIndexOf('@');
    const h = head.lastIndexOf('#');
    const idx = Math.max(a, h);

    if (idx === -1) {
      setOpen(false); setStart(-1); setQ(''); setIx(0);
      return;
    }
    const tail = head.slice(idx + 1);
    if (/\s/.test(tail)) {
      setOpen(false); setStart(-1); setQ(''); setIx(0);
      return;
    }

    setStart(idx);
    setQ(tail);
    setIx(0);
    setOpen(true);
    computeDropdownPos(val, caret);
  };

  const onInput = (e) => {
    const val = e.currentTarget.value;
    const caret = e.currentTarget.selectionStart ?? val.length;
    updateMentionState(val, caret);
  };

  const onScroll = () => {
    const el = textareaRef.current;
    if (!el) return;
    const val = el.value || '';
    const caret = el.selectionStart ?? val.length;
    if (start >= 0) computeDropdownPos(val, caret);
  };

  const insert = (fullName) => {
    const el = textareaRef.current;
    if (!el || start < 0) return;
    const val = el.value || '';
    const caret = el.selectionStart ?? val.length;
    const before = val.slice(0, start);
    const after  = val.slice(caret);
    const next   = `${before}${fullName} ${after}`;
    el.value = next;
    requestAnimationFrame(() => {
      el.focus();
      const posAfter = (before + fullName + ' ').length;
      el.setSelectionRange(posAfter, posAfter);
      setOpen(false); setStart(-1); setQ(''); setIx(0);
    });
  };

  const onKeyDown = (e) => {
    if (!open || matches.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setIx((i) => (i + 1) % matches.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setIx((i) => (i - 1 + matches.length) % matches.length);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      insert(matches[ix].document_name);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      <textarea
        ref={textareaRef}
        rows={3}
        className="card"
        style={{ width: '100%', resize: 'vertical', marginTop: 6 }}
        placeholder="Type @ or # to pick a document. Use ↑/↓ and Enter."
        defaultValue=""
        onInput={onInput}
        onKeyDown={onKeyDown}
        onScroll={onScroll}
        autoComplete="off"
        spellCheck={false}
      />
      <div ref={mirrorRef} aria-hidden="true" />
      {open && matches.length > 0 && (
        <div
          className="mention-dropdown"
          style={{
            position: 'absolute',
            left: pos.left,
            top: pos.top,
            width: pos.width,
            zIndex: 1000,
            background: '#fff',
            border: '1px solid #e5e7eb',
            borderRadius: 8,
            boxShadow: '0 8px 20px rgba(0,0,0,.08)',
            maxHeight: 220,
            overflowY: 'auto'
          }}
          onMouseDown={(e) => e.preventDefault()}
        >
          {matches.map((m, i) => (
            <div
              key={m.document_id}
              className={`mention-item ${i === ix ? 'active' : ''}`}
              style={{
                padding: '8px 10px',
                cursor: 'pointer',
                background: i === ix ? '#f3f4f6' : 'transparent'
              }}
              onClick={() => insert(m.document_name)}
            >
              {m.document_name}
            </div>
          ))}
        </div>
      )}
    </div>
  );
});

export default function PendingMeeting() {
  const [pending, setPending] = useState([]);
  const [resubmitted, setResubmitted] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [date, setDate] = useState({});
  const [saving, setSaving] = useState({});
  const [details, setDetails] = useState({});
  const textRefs = useRef({});

  const token = localStorage.getItem('token');
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000/api';
  const API_ORIGIN = API_BASE_URL.replace(/\/api\/?$/, '');
  const withToken = (url) => {
    const token = localStorage.getItem('token');
    if (!token || !url.includes('/api/files/')) return url;
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}token=${encodeURIComponent(token)}`;
  };

  const normalizeDocuments = (payload) => {
    const docsRaw =
      payload?.documents || payload?.proposalDocuments || payload?.files || payload?.attachments || [];
    if (!Array.isArray(docsRaw)) return [];
    return docsRaw.map((d, idx) => {
      const document_id = d.document_id ?? d.id ?? d.doc_id ?? d.file_id ?? `doc-${idx}`;
      const document_name =
        d.document_name ?? d.name ?? d.file_name ?? d.filename ?? d.title ?? `Document ${idx + 1}`;
      let path = d.path ?? d.url ?? d.file_path ?? d.fileurl ?? d.location ?? '';
      if (path && !/^https?:\/\//.test(path)) {
        path = `${API_ORIGIN}${path}`;
      }
      return { document_id, document_name, path };
    });
  };

  const fetchLists = async () => {
    const [a, b] = await Promise.all([
      axios.get(`${API_BASE_URL}/proposals/list?status=${encodeURIComponent('Pending Meeting')}`, {
        headers: { Authorization: `Bearer ${token}` },
      }),
      axios.get(`${API_BASE_URL}/proposals/list?status=${encodeURIComponent('Resubmitted (Post-Meeting)')}`, {
        headers: { Authorization: `Bearer ${token}` },
      }),
    ]);
    setPending(a.data || []);
    setResubmitted(b.data || []);
  };
  useEffect(() => { fetchLists(); }, []);

  const loadDetails = async (p) => {
    const id = p.proposal_id;
    const detReq = axios.get(`${API_BASE_URL}/proposals/details/${id}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    let meets = { data: [] };
    try {
      meets = await axios.get(`${API_BASE_URL}/proposals/${id}/meetings`, {
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch (e) {
      if (e?.response?.status !== 404) console.error('meetings load failed', e);
    }
    const det = await detReq;
    const apiDetails = det.data || {};
    const documents = normalizeDocuments(apiDetails);
    const generalComments =
      apiDetails.generalComments?.length ? apiDetails.generalComments : (p.generalComments || []);
    return { ...apiDetails, documents, meetings: meets.data || [], generalComments };
  };

  const toggle = async (p) => {
    const id = p.proposal_id;
    const isOpen = !!expanded[id];
    setExpanded((e) => ({ ...e, [id]: !isOpen }));
    if (!isOpen && !details[id]) {
      try {
        const det = await loadDetails(p);
        setDetails((d) => ({ ...d, [id]: det }));
      } catch (e) {
        console.error('details load failed', e);
      }
    }
  };

  const pad = (n) => String(n).padStart(2, '0');
  const toLocalInputValue = (d) =>
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  const [nowLimit, setNowLimit] = useState(toLocalInputValue(new Date()));
  useEffect(() => {
    const id = setInterval(() => setNowLimit(toLocalInputValue(new Date())), 60 * 1000);
    return () => clearInterval(id);
  }, []);

  const recordDecision = async (p, decision) => {
    const id = p.proposal_id;
    const meetingDate = date[id];
    if (!meetingDate) return alert('Please select meeting date/time');

    const overallMessage = textRefs.current[id]?.value || '';
    const body = { meetingDate, decision, note: overallMessage };

    setSaving((s) => ({ ...s, [id]: true }));
    try {
      await axios.post(`${API_BASE_URL}/proposals/${id}/meeting-decision`, body, {
        headers: { Authorization: `Bearer ${token}` },
      });
      await fetchLists();
      setExpanded((e) => ({ ...e, [id]: false }));
      setDetails((d) => { const nd = { ...d }; delete nd[id]; return nd; });
      setDate((d) => { const nd = { ...d }; delete nd[id]; return nd; });
      if (textRefs.current[id]) textRefs.current[id].value = '';
    } catch (e) {
      alert(e?.response?.data?.error || 'Failed to record decision');
    } finally {
      setSaving((s) => ({ ...s, [id]: false }));
    }
  };

  const Section = ({ title, items }) => (
    <div className="card" style={{ marginTop: 16 }}>
      <div className="details-header header-row">
        <h2>{title}</h2>
        <span className="status-pill-header">{items.length} item(s)</span>
      </div>

      {items.map((p) => {
        const id = p.proposal_id;
        const isOpen = !!expanded[id];
        const det = details[id];

        return (
          <div key={id} className="card" style={{ marginTop: 12 }}>
            <div className="details-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <strong style={{ fontSize: 16 }}>{p.title}</strong>
                <span className="status-pill">{p.custom_id || id}</span>
              </div>
              <button
                className="btn sm"
                style={{
                  fontSize: 12,
                  padding: '2px 8px',
                  minWidth: 'auto',
                  width: 'auto',
                  lineHeight: 1.2,
                  whiteSpace: 'nowrap'
                }}
                onClick={() => toggle(p)}
                title={isOpen ? 'Hide details' : 'Show details'}
              >
                {isOpen ? 'Hide' : 'Details'}
              </button>
            </div>

            {isOpen && (
              <div className="card" style={{ marginTop: 8, position: 'relative' }}>
                <div className="details-grid">
                  <div><div className="muted">Title</div><div className="value">{det?.proposal?.title || p.title}</div></div>
                  <div><div className="muted">Status</div><div className="value">{det?.proposal?.status || p.status}</div></div>
                  <div><div className="muted">Ref</div><div className="value">{det?.proposal?.custom_id || p.custom_id || id}</div></div>
                  <div>
                    <div className="muted">Submission Date</div>
                    <div className="value">
                      {(det?.proposal?.submission_date || p.submission_date)
                        ? new Date(det?.proposal?.submission_date || p.submission_date).toLocaleDateString()
                        : '—'}
                    </div>
                  </div>
                </div>

                {!!det?.documents?.length && (
                  <>
                    <div className="section-title" style={{ marginTop: 8 }}>Submitted Documents</div>
                    <div className="document-grid">
                      {det.documents.map((doc) => {
                        const href = doc.path
                          ? withToken(/^https?:\/\//.test(doc.path) ? doc.path : `${API_ORIGIN}${doc.path}`)
                          : '#';
                        return (
                          <a key={doc.document_id} className="document-tile clickable-tile" href={href} target="_blank" rel="noreferrer">
                            <span className="document-name">{doc.document_name}</span>
                          </a>
                        );
                      })}
                    </div>
                  </>
                )}

                <div className="section-title" style={{ marginTop: 12 }}>Overall Message to Researcher</div>
                <MentionBox pid={id} docs={det?.documents || []} ref={(el) => (textRefs.current[id] = el)} />

                <div className="section-title" style={{ marginTop: 12 }}>Meeting Date/Time</div>
                <input
                  type="datetime-local"
                  className="card"
                  style={{ padding: 8, marginTop: 6 }}
                  value={date[id] || ''}
                  max={nowLimit}
                  onChange={(e) => setDate((d) => ({ ...d, [id]: e.target.value }))}
                />

                <div className="actions-grid" style={{ marginTop: 12 }}>
                  <button className="btn primary" disabled={!!saving[id]} onClick={() => recordDecision(p, 'Approve')}>
                    {saving[id] ? 'Saving…' : 'Record Meeting Decision – Approve'}
                  </button>
                  <button className="btn" disabled={!!saving[id]} onClick={() => recordDecision(p, 'Request Revision')}>
                    {saving[id] ? 'Saving…' : 'Record Meeting Decision – Request Revision'}
                  </button>
                </div>

                {!!det?.meetings?.length && (
                  <>
                    <div className="section-title" style={{ marginTop: 16 }}>Meeting Timeline</div>
                    <ul className="comments-list">
                      {det.meetings.map((m) => {
                        const reqs = Array.isArray(m.required_resubmissions) ? m.required_resubmissions : [];
                        const items = reqs.filter((r) => r?.action === 'resubmit');
                        return (
                          <li key={m.id} className="comment-row">
                            <div className="comment-meta">
                              {new Date(m.meeting_date).toLocaleString()} • {m.decision}
                            </div>
                            <div className="comment-text">
                              {m.note || '—'}
                              {!!items.length && (
                                <div style={{ marginTop: 8 }}>
                                  <b>Required resubmissions:</b>
                                  <ul style={{ marginTop: 4, paddingLeft: 18 }}>
                                    {items.map((r, i) => {
                                      const nm =
                                        (det?.documents || []).find(
                                          (d) => String(d.document_id) === String(r.document_id)
                                        )?.document_name || r.document_id;
                                      return <li key={`${m.id}-${i}`}><b>{nm}</b></li>;
                                    })}
                                  </ul>
                                </div>
                              )}
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  </>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );

  return (
    <div className="proposal-details-container">
      <Section title="Pending Meeting" items={pending} />
      <Section title="Resubmitted (Post-Meeting)" items={resubmitted} />
    </div>
  );
}
